import React from 'react';
import style from './tooltip.module.scss';
import ReactTooltip from 'react-tooltip';

const TOOLTIP_SHOW_PREVENTION_DELAY_MS = 150;

interface TooltipProps {
    tooltipId: string;
    className?: string;
    tooltipContents: string | JSX.Element;
    offsetTop: number;
    offsetLeft: number;
}

class Tooltip extends React.Component<TooltipProps, {}> {
    private tooltipRef: Element | null = null;
    private canShowTooltip: boolean = true;

    private showTooltip = () => {
        if (this.canShowTooltip && this.tooltipRef) {
            ReactTooltip.show(this.tooltipRef);
            // Trigger showing the second time to recalculate tooltip position.
            // This fixes positioning fails cause by window resizes.
            ReactTooltip.show(this.tooltipRef);

            this.canShowTooltip = false;
        }
    }

    private hideTooltip = () => {
        if (this.tooltipRef) {
            ReactTooltip.hide(this.tooltipRef);

            /**
             * Only allow showing tooltip after a short pause. This helps handling showing/hiding process on mobile.
             *
             * Note:
             * On mobile, if you flick the screen, starting from the tooltip handle i.e. "What is this ?",
             * then this will produce a series of events that trigger showing and hiding
             * (touchstart -> scroll -> touchend -> scroll).
             * Normally, browsers handle this and hide the tooltip as desired.
             * However, iOS freezes all DOM updates on scroll, which results in a weird behaviour.
             * We were not able to resolve this by rewriting the logic, but preventing hide/show/hide flickers
             * by blocking tooltip showing for some time seems to work reliably.
             */
            setTimeout(() => this.canShowTooltip = true, TOOLTIP_SHOW_PREVENTION_DELAY_MS);
        }
    }

    private globalHidingEvents: string[] = ['touchstart', 'scroll'];

    private addGlobalListeners = () => this.globalHidingEvents.forEach(
        ev => window.addEventListener(ev, this.hideTooltip))
    private removeGlobalListeners = () => this.globalHidingEvents.forEach(
        ev => window.removeEventListener(ev, this.hideTooltip))

    render(): JSX.Element {
        const {tooltipId, className, tooltipContents, children, offsetTop, offsetLeft} = this.props;
        return (
            <span
                className={className}
                data-tip=""
                data-for={tooltipId}
                ref={el => this.tooltipRef = el}
                onTouchEnd={this.showTooltip}
                onMouseEnter={this.showTooltip}
                onMouseLeave={this.hideTooltip}
            >
                {children}
                <ReactTooltip
                    className={style.Tooltip}
                    effect="solid"
                    place="bottom"
                    offset={{top: offsetTop, left: offsetLeft}}
                    id={tooltipId}
                    // ReactTooltip event handling is buggy, so we replace it with our listeners.
                    // Disable scroll listeners.
                    scrollHide={false}
                    // Disable default show/hide listeners by configuring "custom event" functionality
                    // using an irrelevant event.
                    event="emptied"
                    // Add global tooltip hiding event listeners on tooltip showing.
                    afterShow={this.addGlobalListeners}
                    // Remove global listeners on hide for performance and easier debugging.
                    afterHide={this.removeGlobalListeners}
                >
                    {tooltipContents}
                </ReactTooltip>
            </span>
        );
    }
}

export { Tooltip };
